import { useEffect, useState } from "react";
import { Instagram, Hash, AtSign, Camera, Gift, Link2, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";

const Step = ({ n, title, children, icon }: { n: number; title: string; children: React.ReactNode; icon: React.ReactNode }) => (
  <div className="flex gap-4 p-5 border border-border/40 bg-card">
    <div className="shrink-0 w-10 h-10 rounded-none bg-primary/10 border border-primary/30 flex items-center justify-center text-primary font-serif italic font-bold">
      {n}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <h3 className="font-serif text-lg italic font-bold">{title}</h3>
      </div>
      <div className="text-sm text-muted-foreground font-light leading-relaxed space-y-2">{children}</div>
    </div>
  </div>
);

const InstagramInstructions = () => {
  const [igHandle, setIgHandle] = useState("@paridisebeautyinc_");
  const [igHashtag, setIgHashtag] = useState("#Inparadisebeauty");
  const [creditAmount] = useState(5);

  useEffect(() => {
    supabase
      .from("site_settings")
      .select("key, value")
      .in("key", ["instagram_handle", "ig_required_hashtag"])
      .then(({ data }) => {
        data?.forEach((s: any) => {
          if (s.key === "instagram_handle" && s.value) setIgHandle(s.value);
          if (s.key === "ig_required_hashtag" && s.value) setIgHashtag(s.value);
        });
      });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="How to Earn $5 on Instagram | In Paradise Beauty"
        description="Step-by-step guide to share your nails on Instagram and earn a $5 credit toward your next visit."
      />
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-2xl">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-3">Rewards</p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold italic">Share &amp; Earn ${creditAmount}</h1>
            <p className="text-muted-foreground text-sm mt-4 font-light max-w-lg mx-auto">
              Post your fresh look on Instagram and we'll add a <strong className="text-primary">${creditAmount} credit</strong> to your account toward your next appointment.
            </p>
          </div>

          <div className="space-y-4">
            <Step n={1} title="Snap a beautiful photo" icon={<Camera size={18} className="text-primary" />}>
              <p>After your appointment, take a clear, well-lit photo (or short video) of your nails, lashes, or finished look. Natural light works best!</p>
            </Step>

            <Step n={2} title="Post it to your Instagram feed" icon={<Instagram size={18} className="text-primary" />}>
              <p>Open Instagram, tap the <strong>+</strong> icon and select your photo. Posts to your feed (not Stories) qualify for the credit.</p>
            </Step>

            <Step n={3} title={`Tag ${igHandle}`} icon={<AtSign size={18} className="text-primary" />}>
              <p>In your caption, tag us with <strong className="text-primary">{igHandle}</strong>. This lets us see your post and verify your share.</p>
            </Step>

            <Step n={4} title={`Include ${igHashtag}`} icon={<Hash size={18} className="text-primary" />}>
              <p>Add the hashtag <strong className="text-primary">{igHashtag}</strong> anywhere in the caption. This is required to qualify.</p>
            </Step>

            <Step n={5} title="Copy your post link" icon={<Link2 size={18} className="text-primary" />}>
              <p>Once posted, tap the <strong>•••</strong> menu on your post and choose <strong>Copy Link</strong>. The link looks like:</p>
              <code className="block text-xs bg-muted/40 px-2 py-1.5 border border-border/30 break-all">https://www.instagram.com/p/...</code>
            </Step>

            <Step n={6} title="Submit it in your account" icon={<Gift size={18} className="text-primary" />}>
              <p>
                Sign in to your account at <strong>inparadisebeauty.com</strong>, scroll to the <strong>Rewards &amp; Coupons</strong> section, expand <strong>Share on Instagram</strong>, paste your post link, and tap <strong>Submit</strong>.
              </p>
              <p className="text-primary text-xs flex items-start gap-1.5 pt-1">
                <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
                Once approved, your <strong>${creditAmount} credit</strong> appears instantly and can be used at your next checkout.
              </p>
            </Step>
          </div>

          <div className="mt-10 p-5 border border-amber-500/20 bg-amber-500/5">
            <p className="text-xs text-muted-foreground font-light leading-relaxed">
              <strong className="text-foreground">Reminder:</strong> Your post must be public so we can verify the tag and hashtag. One credit is awarded per qualifying post. Credits expire 6 months after issue.
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default InstagramInstructions;
